import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import dayjs, { Dayjs } from 'dayjs';
import { RouteComponentProps } from 'react-router';
import { ErrorBoundary, BasicFilterDropdown, FilterIcon } from '@allenai/varnish';
import { ColumnProps } from 'antd/es/table/Column';
import Table from 'antd/es/table';
import message from 'antd/es/message';
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { RangeValue } from 'rc-picker/es/interface';
import MinusSquareFilled from '@ant-design/icons/MinusSquareFilled';
import PlusSquareFilled from '@ant-design/icons/PlusSquareFilled';

import { TableSansTitleStyle, ScreenReaderOnly, columnRender, PageHeader } from '../components';
import { Assets, Asset, Event, EventType, EventRollup, EventRollupChange } from '../data';
import { getEvents } from '../api';
import { displayDate, getCategoryAndAssetDetails, UiSchemaKey } from '../utils/util';

const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

interface Params {}

const ChangesImpl = (_: RouteComponentProps<Params>) => {
    // get assetSchema for each category of event
    const assetSchemaPropertiesByCategory: { [category: string]: any } = {};
    Object.keys(Assets).forEach((cat) => {
        assetSchemaPropertiesByCategory[cat] = getCategoryAndAssetDetails(
            Assets[cat].category as string
        ).assetSchemaProperties;
    });

    // data fetched from backend
    const [dataSource, setDataSource] = useState<Array<EventRollup>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [dateFilterValue, setDateFilterValue] = useState<RangeValue<Dayjs>>();

    // go get assets
    useEffect(() => {
        loadEvents();
    }, [dateFilterValue]);

    const loadEvents = () => {
        setIsLoading(true);
        getEvents({
            from: dateFilterValue ? dateFilterValue[0]?.format() : undefined,
            to: dateFilterValue ? dateFilterValue[1]?.format() : undefined,
        })
            .then((r) => {
                setDataSource(rollupEvents(r));
            })
            .catch((e) => message.error(`Error loading data: ${e.message}`))
            .finally(() => setIsLoading(false));
    };

    // Take loose events and combine all events for a single id into an EventRollup
    const rollupEvents = (events: Event[]): Array<EventRollup> => {
        const ret: { [index: string]: EventRollup } = {};
        events.forEach((e: Event) => {
            // model rollup after first event
            if (!ret[e.itemId]) {
                ret[e.itemId] = {
                    itemDisplayName: e.itemDisplayName || e.itemId,
                    category: e.category,
                    itemId: e.itemId,
                    rollupEventType: e.eventType,
                    firstUpdate: e.created,
                    lastUpdate: e.created,
                    changes: {},
                    orderedChanges: [],
                };
            }
            // get most important eventType
            // deleted, then created, then edited
            if (ret[e.itemId].rollupEventType !== EventType.Deleted) {
                if (
                    ret[e.itemId].rollupEventType !== EventType.Created ||
                    e.eventType === EventType.Deleted
                ) {
                    ret[e.itemId].rollupEventType = e.eventType;
                }
            }
            const befVals: { [id: string]: any } = e.before !== '' ? JSON.parse(e.before) : {};
            const aftVals: { [id: string]: any } = e.after !== '' ? JSON.parse(e.after) : {};
            // if the current event is later than that last known event, update the rollups last values
            if (dayjs(e.created).diff(dayjs(ret[e.itemId].lastUpdate)) >= 0) {
                ret[e.itemId].lastUpdate = e.created;
                // if we dont have a change event for the current property, model it after the first event we see for this property
                Object.entries(aftVals).forEach(([k, v]: [k: string, v: any]) => {
                    if (!ret[e.itemId].changes[k]) {
                        ret[e.itemId].changes[k] = {
                            category: e.category,
                            property: k,
                            firstValue: befVals[k],
                            lastValue: v,
                            lastUpdated: e.created,
                            lastUpdatedBy: e.updatedBy,
                        };
                    }
                    // then update the last values for this property
                    ret[e.itemId].changes[k].lastValue = v;
                    ret[e.itemId].changes[k].lastUpdated = e.created;
                    ret[e.itemId].changes[k].lastUpdatedBy = e.updatedBy;
                });
            }
            // if the current event is earlier than that earliest known event, update the rollups first values
            if (dayjs(e.created).diff(dayjs(ret[e.itemId].firstUpdate)) <= 0) {
                ret[e.itemId].firstUpdate = e.created;
                // if we dont have a change event for the current property, model it after the first event we see for this property
                Object.entries(befVals).forEach(([k, v]: [k: string, v: any]) => {
                    if (!ret[e.itemId].changes[k]) {
                        ret[e.itemId].changes[k] = {
                            category: e.category,
                            property: k,
                            firstValue: v,
                            lastValue: aftVals[k],
                            lastUpdated: e.created,
                            lastUpdatedBy: e.updatedBy,
                        };
                    }
                    // then update the first values for this property
                    ret[e.itemId].changes[k].firstValue = v;
                });
            }
        });

        // build orderedChanges
        Object.values(ret).forEach((entity) => {
            // order the changes based on the order of props in the spec
            Object.keys(assetSchemaPropertiesByCategory[entity.category]).forEach((prop) => {
                const a = entity.changes[prop];
                // drop values that are not actual changes
                if (a && a.firstValue !== a.lastValue && (!!a.firstValue || !!a.lastValue)) {
                    entity.orderedChanges.push(entity.changes[prop]);
                }
            });
        });

        return Object.values(ret);
    };

    // set up table columns
    const columns: ColumnProps<EventRollup>[] = [
        {
            title: 'Item',
            dataIndex: 'itemDisplayName',
            key: 'itemDisplayName',
            sorter: (a: EventRollup, b: EventRollup) =>
                a.itemDisplayName.localeCompare(b.itemDisplayName),
            render: (value: string, record: EventRollup) => (
                <a href={`/category/${Assets[record.category].category}/${record.itemId}`}>
                    {value}
                </a>
            ),
            width: 420,
            filterDropdown: BasicFilterDropdown,
            filterIcon: FilterIcon,
            onFilter: (filter, record) =>
                record.itemDisplayName.toLowerCase().includes(filter.toString().toLowerCase()),
        },
        {
            title: 'Category',
            dataIndex: 'category',
            key: 'category',
            sorter: (a: EventRollup, b: EventRollup) => a.category.localeCompare(b.category),
            width: 70,
            filters: Object.entries(Assets).map(([k, v]: [k: string, v: Asset]) => {
                return {
                    text: v.label,
                    value: k,
                };
            }),
            filterIcon: FilterIcon,
            onFilter: (filter, record) =>
                record.category.toLowerCase().includes(filter.toString().toLowerCase()),
        },
        {
            title: 'Major Event',
            dataIndex: 'rollupEventType',
            key: 'rollupEventType',
            sorter: (a: EventRollup, b: EventRollup) =>
                a.rollupEventType.localeCompare(b.rollupEventType),
            width: 70,
            filters: Object.keys(EventType)
                .filter((k) => isNaN(Number(k)))
                .map((v) => {
                    return {
                        text: `${v[0].toUpperCase()}${v.slice(1)}`,
                        value: v,
                    };
                }),
            filterIcon: FilterIcon,
            onFilter: (filter, record) =>
                record.rollupEventType.toLowerCase().includes(filter.toString().toLowerCase()),
        },
        {
            title: 'Latest Update',
            dataIndex: 'lastUpdate',
            key: 'lastUpdate',
            sorter: (a: EventRollup, b: EventRollup) => a.lastUpdate.localeCompare(b.lastUpdate),
            render: (value: string) => displayDate(value),
            defaultSortOrder: 'descend',
            width: 70,
        },
    ];

    const expandColumns: ColumnProps<EventRollupChange>[] = [
        {
            title: 'Property',
            dataIndex: 'property',
            key: 'property',
            render: (val: any, record: EventRollupChange) => {
                return <span>{assetSchemaPropertiesByCategory[record.category][val].title}</span>;
            },
            sorter: (a: EventRollupChange, b: EventRollupChange) =>
                a.property.localeCompare(b.property),
            width: 50,
        },
        {
            title: 'Old Value',
            dataIndex: 'firstValue',
            key: 'firstValue',
            render: (val: any, record: EventRollupChange) =>
                categoryPropRender(assetSchemaPropertiesByCategory, val, record),
            width: 265,
        },
        {
            title: 'New Value',
            dataIndex: 'lastValue',
            key: 'lastValue',
            render: (val: any, record: EventRollupChange) =>
                categoryPropRender(assetSchemaPropertiesByCategory, val, record),
            width: 265,
        },
        {
            title: 'User',
            dataIndex: 'lastUpdatedBy',
            key: 'lastUpdatedBy',
            width: 50,
        },
    ];

    const handelDateFilterChange = (newValue: RangeValue<Dayjs>) => {
        setDateFilterValue(newValue);
    };

    return (
        <div>
            <PageHeader iconName="changes" iconType="url" label="Changes">
                <span>
                    <FilterLabel>Changes during:</FilterLabel>
                    <DatePicker.RangePicker
                        size="small"
                        value={dateFilterValue}
                        onChange={handelDateFilterChange}
                    />
                </span>
            </PageHeader>
            <EventRollupTable
                bordered={true}
                locale={{
                    emptyText: () => (
                        <div>
                            <strong>No changes recorded in this date range.</strong>
                            <div>Select a wider range above to see more changes.</div>
                        </div>
                    ),
                }}
                title={() => <ScreenReaderOnly>{`Table of Event Groups`}</ScreenReaderOnly>}
                loading={isLoading}
                rowKey={'itemId'}
                dataSource={dataSource}
                columns={columns}
                scroll={{ x: `630px` }}
                expandable={{
                    expandedRowRender: (eventRollup: EventRollup) => (
                        <EventRollupChangeTable
                            title={() => <ScreenReaderOnly>{`Table of Changes`}</ScreenReaderOnly>}
                            loading={isLoading}
                            rowKey={'property'}
                            dataSource={eventRollup.orderedChanges}
                            columns={expandColumns}
                            pagination={false}
                        />
                    ),
                    expandIcon: ({
                        expanded,
                        onExpand,
                        record,
                    }: {
                        expanded: boolean;
                        onExpand: Function;
                        record: EventRollup;
                    }) =>
                        expanded ? (
                            <MinusIcon onClick={(e) => onExpand(record, e)} />
                        ) : (
                            <PlusIcon onClick={(e) => onExpand(record, e)} />
                        ),
                }}
                pagination={{
                    size: 'small',
                    hideOnSinglePage: true,
                    defaultPageSize: 50,
                    pageSizeOptions: ['5', '10', '20', '50', '100'],
                    showSizeChanger: true,
                }}
            />
        </div>
    );
};

const EventRollupTable = styled((props: any) => <Table<EventRollup> {...props} />)`
    ${TableSansTitleStyle}

    && {
        .ant-table-title {
            border: 0;
        }

        tr.ant-table-expanded-row > td,
        tr.ant-table-expanded-row:hover > td {
            padding: ${({ theme }) =>
                `${theme.spacing.sm} ${theme.spacing.xl} ${theme.spacing.xl} ${theme.spacing.xl}`};

            background-color: ${({ theme }) => theme.color.N3};

            .ant-table,
            th.ant-table-cell {
                background: transparent;
            }

            thead {
                text-transform: uppercase;
                font-size: ${({ theme }) => theme.typography.textStyles.micro.fontSize};
            }
        }
    }
`;

const EventRollupChangeTable = styled((props: any) => <Table<EventRollupChange> {...props} />)`
    ${TableSansTitleStyle};
`;

const categoryPropRender = (
    assetSchemaPropertiesByCategory: { [cat: string]: any },
    val: any,
    record: EventRollupChange
) => {
    // TODO: this is broken for links, to fix, we need to get all entity data for
    // edited entity, but as is its not that bad, the render just uses defaults
    const hasVal = val && (!Array.isArray(val) || val.length > 0);
    return (
        <MaxWidthScroller>
            {hasVal ? (
                columnRender(
                    assetSchemaPropertiesByCategory[record.category][record.property],
                    UiSchemaKey.WidgetDetail
                )(val)
            ) : (
                <Null>NULL</Null>
            )}
        </MaxWidthScroller>
    );
};

const Null = styled.span`
    color: ${({ theme }) => theme.palette.text.secondary};
`;

export const Changes = (props: RouteComponentProps<Params>) => {
    // TODO: Github #108, move error boundary so we dont need to set it inside each new page
    return (
        <ErrorBoundary key="Changes">
            <ChangesImpl {...props} />
        </ErrorBoundary>
    );
};

const FilterLabel = styled.h6`
    display: inline;
    margin-right: ${({ theme }) => theme.spacing.sm};
`;

const PlusIcon = styled(PlusSquareFilled)`
    color: ${({ theme }) => theme.color.B6};
`;

const MinusIcon = styled(MinusSquareFilled)`
    color: ${({ theme }) => theme.color.B6};
`;

const MaxWidthScroller = styled.div`
    max-width: 30vw;
    min-width: 15vw;
    overflow: auto;
`;
